import type { ReactNode } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { Link } from 'react-router-dom';
import { UpgradeGate as UpgradeGateBase, type UpgradePlanKey } from '@pageboss/ui';
import { PLANS } from '@pageboss/shared';
import { PUBLIC_PLAN_ORDER } from '@pageboss/shared/pricing-display';
import { businessRoutes } from '@/lib/route-map';

// =====================================================================
// UpgradeGate — the ONE dashboard-side gate pattern (WP1 Task 12). Every
// linkless "you've hit a limit" / "not on your plan" prose message across
// the dashboard renders this instead of a plain <p> — real "upgrade →" link
// to Billing, plan name from PLANS[planKey].label ONLY (never the internal
// key — the pro-sold-as-"Starter" trap, locked decision #20).
//
// Thin wrapper over @pageboss/ui's UpgradeGateBase: maps this component's
// {capability, planKey, from, variant} onto the base's
// {missing, planKey, billingHref, renderLink}. `capability` is the FULL
// "what's missing" sentence (spec §7 copy pattern) — the base component
// appends "included in the {plan} plan." as its own second line, so callers
// write one honest sentence about what's missing and stop there.
//
// CONSOLIDATION v2 A3 (binding, this task): the original brief's
// `gate.upgrade_shown` / `gate.upgrade_clicked` emitEvent calls are VOID —
// this component emits nothing.
// =====================================================================

export type { UpgradePlanKey };

export interface UpgradeGateProps {
  /** The full "what's missing" sentence, e.g. "Launching a site requires a
   *  paid plan." Rendered verbatim as the gate's first line. */
  capability: string;
  /** INTERNAL plan key that unlocks it — rendered via PLANS[planKey].label
   *  only, never the raw key. */
  planKey: UpgradePlanKey;
  /** Current path, so Billing can return here after checkout. */
  from?: string;
  /** 'inline' (default): compact single-line replacement for the old prose
   *  gate, dropped straight into an existing error/notice slot. 'card': the
   *  full bordered wall (@pageboss/ui's own visual), for a gate that IS the
   *  content of its section (e.g. locations.tsx's "group your locations"
   *  banner). */
  variant?: 'inline' | 'card';
}

/** The next paid tier above `tier`, for "you hit your plan's limit" gates
 *  (sites, pages, AI generations). `free` and the retired `starter` key both
 *  resolve to `pro` — today's entry paid tier (displayed as "Starter", the
 *  #20 trap). Already-at-`growth` has nowhere higher to go; callers on that
 *  tier should not render an upgrade gate at all (there's no plan to point
 *  to — offer an overage pack or plain honest copy instead). Use
 *  `hasNextPaidPlan` to check that before calling this. */
export function nextPaidPlan(tier: string): UpgradePlanKey {
  const idx = PUBLIC_PLAN_ORDER.indexOf(tier as UpgradePlanKey);
  if (idx === -1) return PUBLIC_PLAN_ORDER[0];
  const nextIdx = Math.min(idx + 1, PUBLIC_PLAN_ORDER.length - 1);
  return PUBLIC_PLAN_ORDER[nextIdx] ?? PUBLIC_PLAN_ORDER[0];
}

/** False once `tier` is already the top paid plan — the case where
 *  `nextPaidPlan` would return the SAME tier, which is not a real upgrade. */
export function hasNextPaidPlan(tier: string): boolean {
  return nextPaidPlan(tier) !== tier;
}

function renderLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Link to={href} className="py-1 font-semibold text-brand-700 hover:text-brand-800 hover:underline">
      {children}
    </Link>
  );
}

export function UpgradeGate({ capability, planKey, from, variant = 'inline' }: UpgradeGateProps) {
  // Canonical tenant URLs carry the Business id; these screens are only
  // ever rendered inside one, so it comes from the router, not a prop.
  const businessId = useBusinessId() ?? '';
  const billingHref = businessRoutes.billing(businessId, { plan: planKey, ...(from ? { from } : {}) });

  if (variant === 'card') {
    return (
      <UpgradeGateBase
        missing={capability}
        planKey={planKey}
        billingHref={billingHref}
        renderLink={renderLink}
      />
    );
  }

  // Inline: a compact single line, meant to drop straight into an existing
  // error/notice slot in place of the old plain-text prose gate.
  const planName = PLANS[planKey].label;
  return (
    <p role="note" className="text-sm text-text-secondary" data-gate-plan={planKey}>
      {capability} Included in the {planName} plan.{' '}
      <Link to={billingHref} className="py-1 font-semibold text-brand-700 hover:text-brand-800 hover:underline">
        upgrade to {planName} →
      </Link>
    </p>
  );
}
